import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarClock,
  ShieldAlert,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  X,
  ChevronRight,
  Mail,
} from 'lucide-react';
import api from '../lib/api';
import logger from '../lib/logger';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const STATUS_BADGE_STYLES = {
  delivered: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-blue-100 text-blue-700',
  bounced: 'bg-rose-100 text-rose-700',
  complained: 'bg-rose-100 text-rose-700',
  failed: 'bg-rose-100 text-rose-700',
  delayed: 'bg-amber-100 text-amber-700',
  opened: 'bg-emerald-100 text-emerald-700',
  clicked: 'bg-emerald-100 text-emerald-700',
};

const STATUS_LABELS = {
  delivered: 'Delivered',
  sent: 'Sent',
  bounced: 'Bounced',
  complained: 'Spam',
  failed: 'Failed',
  delayed: 'Delayed',
  opened: 'Opened',
  clicked: 'Clicked',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

const healthScoreClass = (score) => {
  if (score === null || score === undefined) return 'text-slate-400';
  if (score >= 90) return 'text-emerald-600';
  if (score >= 70) return 'text-amber-600';
  return 'text-rose-600';
};

export const DigestHealthWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toastedAlertIds, setToastedAlertIds] = useState(() => new Set());

  const fetchHealth = useCallback(async () => {
    try {
      const response = await api.get('/import/gap-analysis/digest-health');
      setData(response.data || null);
      // Surface unread alerts as a one-time toast on first load this session
      const alerts = response.data?.active_alerts || [];
      setToastedAlertIds((prev) => {
        const next = new Set(prev);
        alerts.forEach((a) => {
          if (next.has(a.id)) return;
          next.add(a.id);
          if (a.severity === 'critical') {
            toast.error(a.title, { description: a.body, duration: 8000 });
          } else {
            toast.warning(a.title, { description: a.body, duration: 6000 });
          }
        });
        return next;
      });
    } catch (err) {
      logger.error('Failed to fetch digest health:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const dismissAlert = async (alert) => {
    try {
      await api.post(`/import/gap-analysis/digest-alerts/${alert.id}/dismiss`);
      setData((prev) => (prev ? {
        ...prev,
        active_alerts: (prev.active_alerts || []).filter((a) => a.id !== alert.id),
      } : prev));
    } catch (err) {
      logger.error('Failed to dismiss digest alert:', err);
      toast.error('Could not dismiss alert');
    }
  };

  if (loading) return null;
  // Don't render the widget at all if user has no schedules — keeps the
  // dashboard clean for users who haven't configured digests yet.
  if (!data || data.total_schedules === 0) return null;

  const score = data.health_score;
  const scoreColor = healthScoreClass(score);

  return (
    <Card
      className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/50 via-white to-white"
      data-testid="digest-health-widget"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-5 w-5 text-indigo-600" />
            Digest Health
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/gap-analysis')}
            data-testid="digest-health-open-btn"
          >
            Open Gap Analysis
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Top stats row */}
        <div className="grid grid-cols-3 gap-3">
          <div
            className="rounded-lg border border-slate-200 bg-white p-3 text-center"
            data-testid="digest-health-score"
          >
            <div className={`text-3xl font-bold ${scoreColor}`}>
              {score === null || score === undefined ? '—' : `${score}%`}
            </div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-1">
              Delivery health
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3 text-center">
            <div className="text-3xl font-bold text-emerald-600">
              {data.active_schedules}
            </div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-1">
              Active{data.paused_schedules > 0 ? ` · ${data.paused_schedules} paused` : ''}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3 text-center">
            <div className="text-sm font-semibold text-slate-700">
              {data.next_run_at ? formatDate(data.next_run_at) : '—'}
            </div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-1">
              Next run
            </div>
          </div>
        </div>

        {/* Active degradation alerts */}
        {(data.active_alerts || []).length > 0 && (
          <div className="space-y-2" data-testid="digest-health-active-alerts">
            {(data.active_alerts || []).map((alert) => {
              const isCritical = alert.severity === 'critical';
              return (
                <div
                  key={alert.id}
                  className={`rounded-lg border p-3 flex items-start gap-3 ${
                    isCritical
                      ? 'border-rose-200 bg-rose-50/70'
                      : 'border-amber-200 bg-amber-50/70'
                  }`}
                  data-testid={`digest-health-alert-${alert.id}`}
                >
                  <AlertTriangle
                    className={`h-4 w-4 mt-0.5 shrink-0 ${
                      isCritical ? 'text-rose-600' : 'text-amber-600'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-xs font-semibold ${
                        isCritical ? 'text-rose-900' : 'text-amber-900'
                      }`}
                    >
                      {alert.title}
                    </div>
                    <div
                      className={`text-[11px] mt-0.5 ${
                        isCritical ? 'text-rose-700/90' : 'text-amber-800/90'
                      }`}
                    >
                      {alert.body}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissAlert(alert)}
                    data-testid={`digest-health-alert-dismiss-${alert.id}`}
                    className="h-7 w-7 p-0 shrink-0"
                    title="Dismiss"
                  >
                    <X className={`h-3.5 w-3.5 ${isCritical ? 'text-rose-700' : 'text-amber-700'}`} />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Suppressions banner */}
        {data.unread_suppressions_count > 0 && (
          <div
            className="rounded-lg border border-rose-200 bg-rose-50/70 p-3 flex items-start gap-3"
            data-testid="digest-health-suppressions"
          >
            <ShieldAlert className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
            <div className="text-xs text-rose-900 flex-1 min-w-0">
              <span className="font-semibold">
                {data.unread_suppressions_count} recipient
                {data.unread_suppressions_count === 1 ? '' : 's'} auto-suppressed
              </span>
              <span className="text-rose-700/80 ml-2">
                {(data.unread_suppressions || []).slice(0, 2).map((s) => s.email).join(', ')}
                {data.unread_suppressions_count > 2
                  ? ` · +${data.unread_suppressions_count - 2} more`
                  : ''}
              </span>
            </div>
          </div>
        )}

        {/* Status summary chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {Object.entries(data.status_summary || {})
            .filter(([, v]) => v > 0)
            .map(([status, count]) => (
              <Badge
                key={status}
                className={STATUS_BADGE_STYLES[status] || 'bg-slate-100 text-slate-700'}
                data-testid={`digest-health-status-${status}`}
              >
                {STATUS_LABELS[status] || status}: {count}
              </Badge>
            ))}
          {(!data.status_summary ||
            Object.values(data.status_summary).every((v) => v === 0)) && (
            <span className="text-xs text-slate-500 italic">
              No recent activity yet — first run on{' '}
              {data.next_run_at ? formatDate(data.next_run_at) : '—'}.
            </span>
          )}
        </div>

        {/* Recent attempts list (top 3) */}
        {data.recent_attempts && data.recent_attempts.length > 0 && (
          <div data-testid="digest-health-recent-attempts">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
              Recent attempts
            </div>
            <ul className="space-y-1.5">
              {data.recent_attempts.slice(0, 3).map((a) => {
                const isHealthy = ['delivered', 'sent', 'opened', 'clicked'].includes(a.status);
                return (
                  <li
                    key={a.id}
                    className="flex items-center gap-2 text-xs text-slate-700 px-2 py-1.5 bg-slate-50 rounded-md"
                  >
                    {isHealthy ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    ) : (
                      <AlertCircle className="h-3.5 w-3.5 text-rose-500 shrink-0" />
                    )}
                    <span className="truncate font-medium">{a.schedule_name || a.schedule_id}</span>
                    <Badge
                      className={`${
                        STATUS_BADGE_STYLES[a.status] || 'bg-slate-100 text-slate-700'
                      } text-[10px] py-0 px-1.5 shrink-0`}
                    >
                      {STATUS_LABELS[a.status] || a.status}
                    </Badge>
                    <span className="ml-auto text-slate-500 flex items-center gap-1 shrink-0">
                      <Mail className="h-3 w-3" />
                      {a.recipients_count}
                      <span className="mx-1">·</span>
                      {formatDate(a.attempted_at)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DigestHealthWidget;
